import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import ROUTES from '@app/src/common/constants/routeUrls';
import { Input, CustomButton, Modifiers } from '@fw-nextgen/fw-components';
import ResetPasswordFormFields from './ResetPasswordFormFields';
import ResetPasswordForm, {
  ResetPasswordFormState
} from './store/ResetPasswordForm';
import ResetPasswordFormIndicators, {
  IResetPasswordIndicator
} from './ResetPasswordFormIndicators';
import PasswordStrengthIndicator from '@app/src/common/components/form/password-strength-indicator/PasswordStrengthIndicator';
import AuthWindow from '../components/AuthWindow';
import AuthWindowLogo from '../components/AuthWindowLogo';

interface IRoute {
  key: string;
}

const ResetPasswordPage = () => {
  const { key } = useParams<IRoute>();
  const history = useHistory();

  const { current: resetPasswordForm } = useRef(
    new ResetPasswordForm() as ResetPasswordFormState
  );

  const setNewPassword = (value: string) => {
    resetPasswordForm.newPassword.setValue(value);
  };

  const setConfirmPassword = (value: string) => {
    resetPasswordForm.confirmPassword.setValue(value);
  };

  const changePassword = () => {
    resetPasswordForm
      .submitForm(key)
      .finally(() => history.push(ROUTES.authPage.path));
  };

  return (
    <AuthWindow className="auth-window_with-small-padding">
      <form className="auth-window__form">
        <AuthWindowLogo className="auth-window__logo" />
        <h5 className="auth-window__heading">
          <FormattedMessage id="reset.pass.form.heading" />
        </h5>
        <Input
          type="password"
          {...ResetPasswordFormFields.newPassword}
          value={resetPasswordForm.newPassword.value}
          error={
            resetPasswordForm.newPassword.isTouched
              ? resetPasswordForm.newPassword.error
              : void 0
          }
          onChange={setNewPassword}
        />
        <Input
          className="auth-window__confirm-password"
          type="password"
          {...ResetPasswordFormFields.confirmPassword}
          value={resetPasswordForm.confirmPassword.value}
          error={
            resetPasswordForm.confirmPassword.isTouched
              ? resetPasswordForm.confirmPassword.error
              : void 0
          }
          onChange={setConfirmPassword}
        />
        <div className="auth-window__indicators">
          {ResetPasswordFormIndicators.map(
            (indicator: IResetPasswordIndicator) => (
              <PasswordStrengthIndicator
                key={indicator.id}
                message={indicator.message}
                checked={resetPasswordForm.newPassword[indicator.name]}
              />
            )
          )}
        </div>
        <CustomButton
          id="ok__btn"
          modifiers={[Modifiers.SMALL, Modifiers.WIDE]}
          className="auth-window__button"
          value="button.save"
          onClick={changePassword}
          disabled={
            !(resetPasswordForm.isValid && resetPasswordForm.isPasswordStrong)
          }
          loading={resetPasswordForm.isLoading}
        />
      </form>
    </AuthWindow>
  );
};

export default observer(ResetPasswordPage);
